// v224 — Build-Info reverse-lookup UI (item 33 Phase C of
// docs/PRODUCT-ROADMAP.md).
//
// Item 66 (Phase C polish) extended this page with the symmetric
// producer-side answer: "which builds in this project PRODUCED
// log4j-2.14.1?". The role toggle (Consumed / Produced / Both) drives
// the SWR fetch's `role=` parameter; produced rows are rendered with
// a deep-link to the matching registry artifact when the sha256
// landed in the `artifacts` table, which gives a SOC operator a
// direct CVE-triage path: pivot from advisory hit → upstream build →
// registry artifact (with its scan / pull / promotion history) in a
// single click.
//
// Three coordinate-shape modes (orthogonal to role):
//
//   coord  — exact-match on the JFrog coordinate (default)
//   prefix — `log4j%` style prefix-match for "all log4j coords"
//   sha256 — exact-match on content hash
//
// Hits are grouped by build so the operator can see at a glance
// which builds touched the coordinate, then drill into a specific
// build for the per-module trail.

import { useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft,
  Activity,
  GitBranch,
  Search,
  Layers,
  Package,
  ArrowDownToLine,
  ArrowUpFromLine,
} from 'lucide-react';
import { Card, CardBody, CardHeader } from '../../components/ui/Card';
import { Input } from '../../components/ui/Input';
import { Badge } from '../../components/ui/Badge';
import { useProjects } from '../../api/client';
import {
  useBuildDependencySearch,
  type DepSearchHit,
  type DepSearchMode,
  type DepSearchResponse,
  type DepSearchRole,
} from '../../api/buildInfo';

function shortRev(rev?: string): string {
  if (!rev) return '';
  return rev.length > 12 ? rev.slice(0, 12) : rev;
}

type TFn = ReturnType<typeof useTranslation>['t'];

function roleHelpText(role: DepSearchRole, t: TFn): string {
  return t(`buildDependencySearch.query.roleHelp.${role}`);
}

function modeLabel(m: DepSearchMode, t: TFn): string {
  return t(`buildDependencySearch.query.modes.${m}`);
}

function roleLabel(rr: DepSearchRole, t: TFn): string {
  return t(`buildDependencySearch.query.roles.${rr}`);
}

function matchingBuildsDescription(
  data: DepSearchResponse | undefined,
  groupCount: number,
  role: DepSearchRole,
  producedCount: number,
  consumedCount: number,
  t: TFn,
): string | undefined {
  if (!data) return undefined;
  const hits = t('buildDependencySearch.matching.hits', { count: data.hits.length });
  const builds = t('buildDependencySearch.matching.acrossBuilds', { count: groupCount });
  const roleBreakdown =
    role === 'any'
      ? ` · ${t('buildDependencySearch.matching.roleBreakdown', { produced: producedCount, consumed: consumedCount })}`
      : '';
  return `${hits} ${builds}${roleBreakdown}`;
}

interface GroupedHit {
  build_id: string;
  build_name: string;
  build_number: string;
  started: string;
  vcs_url?: string;
  vcs_revision?: string;
  consumed: DepSearchHit[];
  produced: DepSearchHit[];
}

function groupByBuild(hits: DepSearchHit[]): GroupedHit[] {
  const map = new Map<string, GroupedHit>();
  for (const h of hits) {
    let g = map.get(h.build_id);
    if (!g) {
      g = {
        build_id: h.build_id,
        build_name: h.build_name,
        build_number: h.build_number,
        started: h.started,
        vcs_url: h.vcs_url,
        vcs_revision: h.vcs_revision,
        consumed: [],
        produced: [],
      };
      map.set(h.build_id, g);
    }
    if (h.role === 'produced') {
      g.produced.push(h);
    } else {
      g.consumed.push(h);
    }
  }
  return Array.from(map.values());
}

function BuildGroupRow({ g, projectId }: Readonly<{ g: GroupedHit; projectId: string | undefined }>) {
  const { t } = useTranslation();
  return (
    <div className="px-5 py-3 space-y-2">
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <div>
          <Link
            to={`/projects/${projectId}/builds/${encodeURIComponent(g.build_name)}/${encodeURIComponent(g.build_number)}`}
            className="font-medium text-fg hover:text-accent"
          >
            {g.build_name}
            <span className="text-fg-muted font-mono"> #{g.build_number}</span>
          </Link>
          <span className="text-xs text-fg-muted ml-3">
            {new Date(g.started).toLocaleString()}
          </span>
          {g.produced.length > 0 && (
            <Badge tone="success" className="ml-2">
              {t('buildDependencySearch.badges.produced', { count: g.produced.length })}
            </Badge>
          )}
          {g.consumed.length > 0 && (
            <Badge tone="neutral" className="ml-2">
              {t('buildDependencySearch.badges.consumed', { count: g.consumed.length })}
            </Badge>
          )}
        </div>
        {g.vcs_revision && (
          <span className="inline-flex items-center gap-1.5 text-xs text-fg-muted">
            <GitBranch className="h-3.5 w-3.5 shrink-0" />
            <code className="font-mono">{shortRev(g.vcs_revision)}</code>
          </span>
        )}
      </div>
      {g.produced.length > 0 && (
        <ul className="text-xs text-fg-muted space-y-0.5">
          {g.produced.map((row, i) => (
            <li
              key={`prod/${row.module_id}/${row.artifact_name ?? ''}/${i}`}
              className="flex items-center gap-2 flex-wrap"
            >
              <ArrowUpFromLine className="h-3 w-3 text-success shrink-0" />
              <code className="font-mono text-fg">{row.module_id}</code>
              {row.artifact_name && (
                <>
                  <span className="text-fg-subtle">→</span>
                  <code className="font-mono">{row.artifact_name}</code>
                </>
              )}
              {row.dep_type && <Badge tone="neutral">{row.dep_type}</Badge>}
              {row.artifact_id && (
                <Link
                  to={`/artifacts/${encodeURIComponent(row.artifact_id)}`}
                  className="inline-flex items-center gap-1 text-accent hover:underline"
                >
                  <Package className="h-3 w-3" />
                  <span className="font-mono">{row.artifact_path || t('buildDependencySearch.inRegistry')}</span>
                </Link>
              )}
            </li>
          ))}
        </ul>
      )}
      {g.consumed.length > 0 && (
        <ul className="text-xs text-fg-muted space-y-0.5">
          {g.consumed.map((row, i) => (
            <li
              key={`cons/${row.module_id}/${row.dep_id}/${row.dep_scope ?? ''}/${i}`}
              className="flex items-center gap-2 flex-wrap"
            >
              <ArrowDownToLine className="h-3 w-3 text-fg-subtle shrink-0" />
              <code className="font-mono">{row.module_id}</code>
              <span className="text-fg-subtle">→</span>
              <code className="font-mono text-fg">{row.dep_id}</code>
              {row.dep_scope && <Badge tone="neutral">{row.dep_scope}</Badge>}
              {row.dep_type && <Badge tone="neutral">{row.dep_type}</Badge>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function BuildDependencySearch() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const { data: projects } = useProjects();
  const project = projects?.find((p) => p.id === id);

  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get('q') ?? '';
  const modeParam = (searchParams.get('mode') as DepSearchMode | null) ?? 'coord';
  const mode: DepSearchMode =
    modeParam === 'sha256' || modeParam === 'prefix' ? modeParam : 'coord';
  const roleParam = (searchParams.get('role') as DepSearchRole | null) ?? 'consumed';
  const role: DepSearchRole =
    roleParam === 'produced' || roleParam === 'any' ? roleParam : 'consumed';

  const { data, error, isLoading } = useBuildDependencySearch(id, query, mode, role);
  const groups = useMemo<GroupedHit[]>(
    () => (data ? groupByBuild(data.hits) : []),
    [data],
  );

  const consumedCount = useMemo(
    () => (data ? data.hits.filter((h) => h.role === 'consumed').length : 0),
    [data],
  );
  const producedCount = useMemo(
    () => (data ? data.hits.filter((h) => h.role === 'produced').length : 0),
    [data],
  );

  function setParam(key: string, value: string) {
    const next = new URLSearchParams(searchParams);
    if (value) {
      next.set(key, value);
    } else {
      next.delete(key);
    }
    setSearchParams(next, { replace: true });
  }

  const roleHelp = roleHelpText(role, t);

  return (
    <div className="space-y-6">
      <div>
        <Link
          to={`/projects/${id}/builds`}
          className="inline-flex items-center gap-2 text-sm text-fg-muted hover:text-fg mb-2"
        >
          <ArrowLeft className="h-4 w-4" />
          {t('buildDependencySearch.back')}
        </Link>
        <p className="orb-eyebrow text-accent/80 mb-1">
          {project?.slug ?? id} · {t('buildDependencySearch.eyebrowSuffix')}
        </p>
        <h1 className="text-2xl font-display font-semibold text-fg leading-tight inline-flex items-center gap-2">
          <Search className="h-5 w-5 text-accent" />
          {t('buildDependencySearch.title')}
        </h1>
        <p className="text-fg-muted text-sm mt-2 max-w-3xl">
          <Trans
            i18nKey="buildDependencySearch.intro"
            components={[<code key={0} className="font-mono" />]}
          />
        </p>
      </div>

      <Card>
        <CardHeader
          title={t('buildDependencySearch.query.title')}
          description={t('buildDependencySearch.query.description')}
        />
        <CardBody>
          <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-3">
            <Input
              placeholder={t('buildDependencySearch.query.placeholder')}
              value={query}
              onChange={(e) => setParam('q', e.target.value)}
            />
            <div className="inline-flex items-center gap-1 rounded-md border border-border bg-bg-elevated p-1">
              {(['coord', 'prefix', 'sha256'] as DepSearchMode[]).map((m) => (
                <button
                  key={m}
                  type="button"
                  onClick={() => setParam('mode', m === 'coord' ? '' : m)}
                  className={
                    'px-3 py-1.5 text-xs rounded-sm transition-colors ' +
                    (mode === m
                      ? 'bg-accent/15 text-accent'
                      : 'text-fg-muted hover:text-fg')
                  }
                >
                  {modeLabel(m, t)}
                </button>
              ))}
            </div>
          </div>
          <p className="text-xs text-fg-subtle mt-2">
            {t(`buildDependencySearch.query.modeHelp.${mode}`)}
          </p>

          <div className="mt-4">
            <p className="text-xs font-medium text-fg-muted mb-1.5">{t('buildDependencySearch.query.roleLabel')}</p>
            <div className="inline-flex items-center gap-1 rounded-md border border-border bg-bg-elevated p-1">
              {(['consumed', 'produced', 'any'] as DepSearchRole[]).map((rr) => (
                <button
                  key={rr}
                  type="button"
                  onClick={() => setParam('role', rr === 'consumed' ? '' : rr)}
                  className={
                    'px-3 py-1.5 text-xs rounded-sm transition-colors inline-flex items-center gap-1.5 ' +
                    (role === rr
                      ? 'bg-accent/15 text-accent'
                      : 'text-fg-muted hover:text-fg')
                  }
                >
                  {rr === 'consumed' && <ArrowDownToLine className="h-3 w-3" />}
                  {rr === 'produced' && <ArrowUpFromLine className="h-3 w-3" />}
                  {rr === 'any' && <Layers className="h-3 w-3" />}
                  {roleLabel(rr, t)}
                </button>
              ))}
            </div>
            <p className="text-xs text-fg-subtle mt-2">{roleHelp}</p>
          </div>
        </CardBody>
      </Card>

      <Card>
        <CardHeader
          title={
            <span className="inline-flex items-center gap-2">
              <Activity className="h-4 w-4 text-accent" />
              {t('buildDependencySearch.matching.header')}
            </span>
          }
          description={matchingBuildsDescription(data, groups.length, role, producedCount, consumedCount, t)}
        />
        {!query.trim() && (
          <CardBody>
            <p className="text-fg-muted text-sm">
              {t('buildDependencySearch.matching.prompt')}
            </p>
          </CardBody>
        )}
        {query.trim() && isLoading && (
          <CardBody>
            <p className="text-fg-muted text-sm">{t('buildDependencySearch.matching.searching')}</p>
          </CardBody>
        )}
        {error && (
          <CardBody>
            <p className="text-danger font-mono text-sm">{String(error)}</p>
          </CardBody>
        )}
        {data && groups.length === 0 && !isLoading && (
          <CardBody>
            <p className="text-fg-muted text-sm">
              <Trans
                i18nKey={`buildDependencySearch.matching.noBuilds.${role}`}
                values={{ query: data.query }}
                components={[<code key={0} className="font-mono" />]}
              />
            </p>
          </CardBody>
        )}
        {groups.length > 0 && (
          <div className="divide-y divide-border">
            {groups.map((g) => (
              <BuildGroupRow key={g.build_id} g={g} projectId={id} />
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
